import { z } from 'zod';
import type { PlaycastMessage, PlaycastMessageTarget } from '../../messages';
import { connectedHostSchema, localHostDescriptorSchema } from './hostSelection';

export const LOCAL_EDITOR_PROTOCOL_VERSION = 1 as const;
export const LOCAL_EDITOR_PORTS = [27130, 27131] as const;
export const LOCAL_EDITOR_PATH = '/macro-editor' as const;
export const LOCAL_EDITOR_MAX_FRAME_BYTES = 1024 * 1024;

/**
 * Raw macro packages served by host-electron in local mode. `file` accepts byte ranges and must name a
 * manifest-listed file or a `regions/frames/**` PNG.
 */
export const LOCAL_RAW_PACKAGE_ROUTES = {
  list: `${LOCAL_EDITOR_PATH}/raw-packages`,
  file: `${LOCAL_EDITOR_PATH}/raw-packages/:packageId/*`,
} as const;

export function localRawPackageFilePath(packageId: string, file: string): string {
  const encoded = file.split('/').map(segment => encodeURIComponent(segment)).join('/');
  return `${LOCAL_RAW_PACKAGE_ROUTES.list}/${encodeURIComponent(packageId)}/${encoded}`;
}

const idSchema = z.string().min(1).max(128);
const sessionIdSchema = z.string().uuid();
const localSignalSchema = z.discriminatedUnion('type', [
  z.object({ type: z.enum(['offer', 'answer']), sdp: z.string().min(1).max(256 * 1024) }).strict(),
  z.object({ type: z.literal('ice'), candidate: z.string().min(1).max(16 * 1024) }).strict(),
]);

const playcastEnvelopeSchema = z.object({
  header: z.object({ target: z.string().min(1), action: z.string().min(1) }).passthrough(),
  body: z.object({ message: z.unknown() }).passthrough(),
}).passthrough();

export const localEditorRequestSchema = z.discriminatedUnion('op', [
  z.object({ v: z.literal(1), id: idSchema, op: z.literal('discover') }).strict(),
  z.object({ v: z.literal(1), id: idSchema, op: z.literal('connect') }).strict(),
  z.object({ v: z.literal(1), id: idSchema, op: z.literal('reconnectVideo'), sessionId: sessionIdSchema }).strict(),
  z.object({ v: z.literal(1), id: idSchema, op: z.literal('signal'), sessionId: sessionIdSchema,
    generation: z.number().int().nonnegative().optional(), signal: localSignalSchema }).strict(),
  z.object({ v: z.literal(1), id: idSchema, op: z.literal('message'), sessionId: sessionIdSchema,
    message: playcastEnvelopeSchema }).strict(),
  z.object({ v: z.literal(1), id: idSchema, op: z.literal('disconnect'), sessionId: sessionIdSchema }).strict(),
]);

export const localEditorEventSchema = z.discriminatedUnion('event', [
  z.object({ v: z.literal(1), id: idSchema, event: z.literal('discovered'), host: localHostDescriptorSchema }).strict(),
  z.object({ v: z.literal(1), id: idSchema, event: z.literal('connected'), host: connectedHostSchema }).strict(),
  z.object({ v: z.literal(1), id: idSchema, event: z.literal('videoReset'), sessionId: sessionIdSchema,
    generation: z.number().int().positive() }).strict(),
  z.object({ v: z.literal(1), event: z.literal('signal'), sessionId: sessionIdSchema,
    generation: z.number().int().nonnegative().optional(), signal: localSignalSchema }).strict(),
  z.object({ v: z.literal(1), event: z.literal('message'), sessionId: sessionIdSchema,
    raw: z.string().max(LOCAL_EDITOR_MAX_FRAME_BYTES) }).strict(),
  z.object({ v: z.literal(1), event: z.literal('disconnected'), sessionId: sessionIdSchema,
    reason: z.string().min(1).max(512) }).strict(),
  z.object({ v: z.literal(1), id: idSchema, event: z.literal('error'), code: z.string().min(1).max(128),
    message: z.string().min(1).max(1024) }).strict(),
]);

export type LocalSignal = z.infer<typeof localSignalSchema>;
export type LocalEditorRequest = z.infer<typeof localEditorRequestSchema> & {
  message?: PlaycastMessage<PlaycastMessageTarget>;
};
export type LocalEditorEvent = z.infer<typeof localEditorEventSchema>;

export function parseLocalEditorRequest(raw: string): LocalEditorRequest {
  if (new TextEncoder().encode(raw).byteLength > LOCAL_EDITOR_MAX_FRAME_BYTES) {
    throw new Error('local_editor_frame_too_large');
  }
  let value: unknown;
  try { value = JSON.parse(raw); } catch { throw new Error('local_editor_invalid_json'); }
  return localEditorRequestSchema.parse(value) as LocalEditorRequest;
}
